import type { CardFee } from "Components/Merchant/FeeCalculator/constants";

type AverageCardFeesArgs = {
  cardFees: Array<CardFee>;
  currentProviderFee: number;
  suggestedProviderFee: number;
  percentageOfPersonalCards: number;
};

type AverageCardFeesResult = {
  averageCurrentCardFee: number;
  averageSuggestedCardFee: number;
};

export const getAverageCardFees = ({
  cardFees,
  currentProviderFee,
  suggestedProviderFee,
  percentageOfPersonalCards,
}: AverageCardFeesArgs): AverageCardFeesResult => {
  const percentageOfCommercialCards = 100 - percentageOfPersonalCards;
  const personalCardFees = cardFees.filter(
    ({ category }) => category === "Personal",
  );
  const commercialCardFees = cardFees.filter(
    ({ category }) => category === "Commercial",
  );

  const { personalCurrentTotal, personalSuggestedTotal } =
    personalCardFees.reduce(
      (acc, { interchange, schemeFee }) => ({
        personalCurrentTotal:
          acc.personalCurrentTotal +
          interchange +
          schemeFee +
          currentProviderFee,
        personalSuggestedTotal:
          acc.personalSuggestedTotal +
          interchange +
          schemeFee +
          suggestedProviderFee,
      }),
      { personalCurrentTotal: 0, personalSuggestedTotal: 0 },
    );
  const averageCurrentPersonalCardFee =
    personalCurrentTotal / personalCardFees.length;
  const averageSuggestedPersonalCardFee =
    personalSuggestedTotal / personalCardFees.length;

  const { commercialCurrentTotal, commercialSuggestedTotal } =
    commercialCardFees.reduce(
      (acc, { interchange, schemeFee }) => ({
        commercialCurrentTotal:
          acc.commercialCurrentTotal +
          interchange +
          schemeFee +
          currentProviderFee,
        commercialSuggestedTotal:
          acc.commercialSuggestedTotal +
          interchange +
          schemeFee +
          suggestedProviderFee,
      }),
      { commercialCurrentTotal: 0, commercialSuggestedTotal: 0 },
    );
  const averageCurrentCommercialCardFee =
    commercialCurrentTotal / commercialCardFees.length;
  const averageSuggestedCommercialCardFee =
    commercialSuggestedTotal / commercialCardFees.length;

  const averageCurrentCardFee =
    (averageCurrentPersonalCardFee * percentageOfPersonalCards +
      averageCurrentCommercialCardFee * percentageOfCommercialCards) /
    100;

  const averageSuggestedCardFee =
    (averageSuggestedPersonalCardFee * percentageOfPersonalCards +
      averageSuggestedCommercialCardFee * percentageOfCommercialCards) /
    100;

  return {
    averageCurrentCardFee,
    averageSuggestedCardFee,
  };
};

type CalculateYearlyCostsArgs = {
  fee: number;
  fixedTransactionCost: number;
  averageTransactionValue: number;
  numberOfTransactionsPerYear: number;
};

export const calculateYearlyCosts = ({
  fee,
  fixedTransactionCost,
  averageTransactionValue,
  numberOfTransactionsPerYear,
}: CalculateYearlyCostsArgs) =>
  (fee * (averageTransactionValue / 100) + fixedTransactionCost) *
  numberOfTransactionsPerYear;
